import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  createResource,
  deleteResource,
  resourceFormQuery,
  resourceQuery,
  resourcesQuery,
  saveResource,
  type ResourceModule,
} from '../../api/settingsUsers'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Forbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { esc, failed, tableRequest } from './shared'

/**
 * Resources Management - `active/list_resources.php` (lista + modala "Add New
 * Resource"), `active/resource_tabs.php` (randul desfasurat, cu o singura fila)
 * si `active/edit_resource.php`.
 *
 * O RESURSA e o bucata din `modul.resursa.drept`, adica din chiar cheile pe
 * care le cere `canAccess`. De-aia formularul de editare intreaba inca o data
 * inainte sa scrie ("Altered data may cause serious problems on portal access
 * permisions") si tot de-aia toate capetele cer `settings.user_accounts.delete`,
 * treapta cea mai de sus - asa era si in portal.
 *
 * SLUG-UL se scrie singur din numele modulului si al resursei
 * (`<slug modul>.<nume_resursa_cu_underscore>`), si ramane de mana daca omul il
 * schimba - ca in machetele vechi. Unicitatea o verifica SERVERUL; in portal,
 * la editare, regula era comentata si nu se verifica nimic.
 */

const ORDER_NAMES = ['', 'id', 'name', 'slug', 'module_name', '']

function resourceColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 30,
      html: (row) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(row.id)}" data-type=""><i class="fas fa-plus-circle"></i></center>`,
    },
    { index: 1, title: 'ID', orderable: true, visible: true, width: 30, className: 'a-left', html: (row) => esc(row.id) },
    {
      index: 2,
      title: 'Resource Name',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(row.name),
    },
    { index: 3, title: 'Slug', orderable: true, visible: true, className: 'a-left', html: (row) => esc(row.slug) },
    {
      index: 4,
      title: 'Module Name',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(row.module_name),
    },
    {
      index: 5,
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 130,
      html: (row) =>
        `<a href="javascript:void(0)" data-id="${Number(row.id)}" data-slug="${esc(row.slug)}" class="btn btn-xs btn-danger od-delete-resource pull-left"> <i class="fas fa-times" aria-hidden="true"></i> Delete </a>`,
    },
  ]
}

export function ResourcesPanel() {
  const client = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [state, setState] = useTableState('settings-users-resources', {
    start: 0,
    length: 25,
    order: { column: 1, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => resourceColumns(), [])
  const table = useQuery({
    ...resourcesQuery(tableRequest(state, ORDER_NAMES, 'id')),
    placeholderData: keepPreviousData,
  })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]
  const reload = () => void client.invalidateQueries({ queryKey: ['settings', 'users', 'resources'] })

  const drop = (id: number, slug: string) => {
    if (!confirm('Delete this resource? Rights that use it stop working.')) return
    void deleteResource(id, slug)
      .then(() => {
        toast('Resource deleted.', 'Success')
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <div id="content">
      <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setAdding(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Add New Resource
      </button>
      <h4>
        <strong>Resources</strong> Management
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit <strong>resources</strong>
      </label>
      <hr />

      <div
        onClick={(event) => {
          const hit = (event.target as HTMLElement).closest('.od-delete-resource')
          if (!hit) return
          drop(Number(hit.getAttribute('data-id') ?? 0), hit.getAttribute('data-slug') ?? '')
        }}
      >
        <DataTable
          id="settings-users-resources"
          columns={columns}
          rows={rows}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          error={table.error ? failed(table.error) : null}
          onRetry={() => void table.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => `row_${Number(row.id)}`}
          emptyHint="A resource is one piece of a right key, like inventoree.devices."
          details={(row) => <ResourceWorkspace resourceId={Number(row.id)} onSaved={reload} />}
        />
      </div>

      <NewResourceModal
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={() => {
          setAdding(false)
          reload()
        }}
      />
    </div>
  )
}

/** `resource_tabs.php`: o singura fila, "Edit Resource". */
function ResourceWorkspace({ resourceId, onSaved }: { resourceId: number; onSaved(): void }) {
  const page = useQuery(resourceQuery(resourceId))

  if (page.error) return <div className="alert alert-danger">{failed(page.error)}</div>
  if (!page.data) return <Spinner />

  return (
    <div className="row">
      <div className="col-md-12">
        <ul className="nav nav-tabs managementList" role="tablist">
          <li className="active">
            <a href="#edit" onClick={(e) => e.preventDefault()}>
              <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit Resource
            </a>
          </li>
        </ul>
        <div className="tab-content p-3">
          <ResourceForm
            modules={page.data.modules}
            resource={page.data.resource}
            onSubmit={(fields) => saveResource(resourceId, fields)}
            submitLabel="Edit"
            /* Intreaba inainte, ca in `edit_resource.php`. */
            confirmText="Are you sure you want to edit this resource? Altered data may cause serious problems on portal access permisions."
            onSaved={() => {
              toast('Resource edited.', 'Success')
              onSaved()
            }}
          />
        </div>
      </div>
    </div>
  )
}

function NewResourceModal({ open, onClose, onSaved }: { open: boolean; onClose(): void; onSaved(): void }) {
  const form = useQuery({ ...resourceFormQuery, enabled: open })

  return (
    <Modal id="newResource" open={open} onClose={onClose} title="Add New Resource" labelledBy="newResourceLabel" large>
      {form.data ? (
        <ResourceForm
          modules={form.data.modules}
          resource={null}
          onSubmit={(fields) => createResource(fields)}
          submitLabel="Create"
          onClose={onClose}
          onSaved={() => {
            toast('Resource created.', 'Success')
            onSaved()
          }}
        />
      ) : (
        <Spinner />
      )}
    </Modal>
  )
}

/** Numele resursei -> slug, ca in `list_resources.php` (`keyup`). */
function slugFor(moduleSlug: string, name: string): string {
  const cleaned = name.replace(/[^a-zA-Z0-9 ]/g, '').replace(/ /g, '_').toLowerCase()
  return `${moduleSlug}.${cleaned}`
}

function ResourceForm({
  modules,
  resource,
  onSubmit,
  submitLabel,
  confirmText,
  onClose,
  onSaved,
}: {
  modules: ResourceModule[]
  resource: { id: number; moduleId: number; name: string; slug: string } | null
  onSubmit(fields: Record<string, string>): Promise<unknown>
  submitLabel: string
  confirmText?: string
  onClose?(): void
  onSaved(): void
}) {
  const [moduleId, setModuleId] = useState(resource ? String(resource.moduleId) : '')
  const [name, setName] = useState(resource?.name ?? '')
  const [slug, setSlug] = useState(resource?.slug ?? '')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setModuleId(resource ? String(resource.moduleId) : '')
    setName(resource?.name ?? '')
    setSlug(resource?.slug ?? '')
  }, [resource])

  const moduleSlug = modules.find((m) => String(m.id) === moduleId)?.slug ?? ''

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (confirmText && !confirm(confirmText)) return
    setBusy(true)
    void onSubmit({ module: moduleId, module_slug: moduleSlug, resource_name: name, resource_slug: slug })
      .then(() => onSaved())
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <form method="post" onSubmit={submit}>
      <div className="row">
        <div className={resource ? 'col-md-12' : 'col-md-8'}>
          <div className="form-group">
            <label className="control-label">
              Module <span className="required">*</span>
            </label>
            <select
              className="form-control input-sm"
              required
              value={moduleId}
              onChange={(event) => {
                const next = event.target.value
                setModuleId(next)
                const nextSlug = modules.find((m) => String(m.id) === next)?.slug ?? ''
                setSlug(name === '' ? nextSlug + '.' : slugFor(nextSlug, name))
              }}
            >
              <option value=""> - choose assigned module - </option>
              {modules.map((module) => (
                <option key={module.id} value={module.id}>
                  {module.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {resource ? null : (
          <div className="col-md-4">
            <div className="form-group">
              <label className="control-label">
                Module Slug <span className="required">*</span>
              </label>
              <input type="text" className="form-control input-sm" readOnly value={moduleSlug} />
            </div>
          </div>
        )}

        <div className="col-md-12">
          <div className="form-group">
            <label className="control-label">
              Resource Name <span className="required">*</span>
            </label>
            <input
              type="text"
              className="form-control input-sm"
              required
              value={name}
              onChange={(event) => {
                setName(event.target.value)
                setSlug(slugFor(moduleSlug, event.target.value))
              }}
            />
          </div>
        </div>

        <div className="col-md-12">
          <div className="form-group">
            <label className="control-label">
              Resource Slug <span className="required">*</span> <small>(unique, auto generated, editable)</small>
            </label>
            <input
              type="text"
              className="form-control input-sm bg-lightseagreen"
              required
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
          </div>
        </div>

        <div className="col-md-12">
          <div className="form-group mt-2 text-center center-block">
            {onClose ? (
              <>
                <button type="button" className="btn btn-default" data-dismiss="modal" onClick={onClose}>
                  Close
                </button>{' '}
              </>
            ) : null}
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Saving...' : submitLabel}
            </button>
          </div>
        </div>
      </div>
    </form>
  )
}
